import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Column } from 'primereact/column'
import styled from 'styled-components'

import useGetData from 'Hooks/useGetData'
import { Button, SearchInput } from 'Components/input'
import { ModalActionsFooter, Panel, ToolPanel } from 'Components/readonly'
import { LineHeading, Section, Spacer, ThinHeaderDataTable } from 'Components/layout'
import { TableMarkings } from 'Components/readonly/markings'

const TABLE_PAGE_SIZE = 5
const TABLE_ROW_HEIGHT_PX = 66
const TABLE_HEADER_HEIGHT_PX = 42
const TABLE_BODY_MIN_HEIGHT_PX = TABLE_HEADER_HEIGHT_PX + TABLE_PAGE_SIZE * TABLE_ROW_HEIGHT_PX

const TableFrame = styled.div`
  .p-datatable-wrapper {
    min-height: ${TABLE_BODY_MIN_HEIGHT_PX}px !important;
  }
`

const ResultsTable = styled(ThinHeaderDataTable)`
  &&& {
    width: 100%;

    table {
      table-layout: fixed;
      width: 100%;
    }

    &.p-datatable .p-datatable-wrapper {
      min-height: ${TABLE_BODY_MIN_HEIGHT_PX}px;
    }

    .p-datatable-tbody > tr {
      height: ${TABLE_ROW_HEIGHT_PX}px;
    }

    .p-datatable-tbody > tr > td,
    .p-datatable-emptymessage td {
      height: ${TABLE_ROW_HEIGHT_PX}px;
      box-sizing: border-box;
      vertical-align: middle;
      line-height: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* Inline pictograms otherwise sit on the text baseline and add ~1px per row. */
    .p-datatable-tbody > tr > td > * {
      vertical-align: middle;
    }
  }
`

const sortChemicals = (chemicals, sortField, sortOrder) => {
  const list = Array.isArray(chemicals) ? [...chemicals] : []
  if (!sortField || !sortOrder) {
    return list
  }

  list.sort((left, right) => {
    const leftValue = left[sortField]
    const rightValue = right[sortField]
    let result = 0

    if (leftValue == null && rightValue != null) {
      result = -1
    } else if (leftValue != null && rightValue == null) {
      result = 1
    } else if (typeof leftValue === 'string' || typeof rightValue === 'string') {
      result = String(leftValue ?? '').localeCompare(String(rightValue ?? ''), undefined, { numeric: true })
    } else if (leftValue < rightValue) {
      result = -1
    } else if (leftValue > rightValue) {
      result = 1
    }

    return result * sortOrder
  })

  return list
}

const padPageRows = (rows) => {
  if (rows.length === 0 || rows.length >= TABLE_PAGE_SIZE) {
    return rows
  }

  const padded = [...rows]
  while (padded.length < TABLE_PAGE_SIZE) {
    padded.push({ __placeholder: true, id: `__placeholder_${padded.length}` })
  }
  return padded
}

export default ({ modalController, onAddProduct, onRegisterNewProduct }) => {
  const intl = useIntl()
  const location = modalController.data
  const [first, setFirst] = useState(0)
  const [sortField, setSortField] = useState(null)
  const [sortOrder, setSortOrder] = useState(null)
  const [searchText, setSearchText] = useState('')

  const [isLoading, chemicals] = useGetData(
    '/rapi/chemicals/search?search=' + encodeURIComponent(searchText),
    []
  )

  useEffect(() => {
    setFirst(0)
  }, [searchText])

  const sortedChemicals = useMemo(
    () => sortChemicals(chemicals, sortField, sortOrder),
    [chemicals, sortField, sortOrder]
  )

  const pageRows = useMemo(
    () => padPageRows(sortedChemicals.slice(first, first + TABLE_PAGE_SIZE)),
    [sortedChemicals, first]
  )

  const addBody = (row) =>
    row.__placeholder ? null : (
      <Button
        primary
        icon="pi pi-plus-circle"
        onClick={() => {
          modalController.close()
          onAddProduct(location, row)
        }}
      />
    )

  const handleRegisterNewProduct = () => {
    modalController.close()
    onRegisterNewProduct(location)
  }

  return (
    <div>
      <Section>
        <LineHeading>
          <FormattedMessage id="common.location"/>
        </LineHeading>{' '}
        {location.location}
      </Section>

      <Section>
        <FormattedMessage id="inventory.add-product-text"/>
      </Section>

      <Section>
        <Panel>
          <h3>
            <FormattedMessage id="inventory.search-product-title"/>
          </h3>
          <ToolPanel>
            <SearchInput
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </ToolPanel>
          <TableFrame>
            <ResultsTable
              className="flextable"
              value={pageRows}
              lazy={true}
              paginator={true}
              alwaysShowPaginator={true}
              rows={TABLE_PAGE_SIZE}
              totalRecords={sortedChemicals.length}
              loading={isLoading}
              first={first}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={(e) => {
                setSortField(e.sortField)
                setSortOrder(e.sortOrder)
              }}
              onPage={(e) => setFirst(e.first)}>
              <Column
                key="article_number"
                field="article_number"
                header={intl.formatMessage({ id: 'chemicals.article_id' })}
                sortable={true}
                style={{ width: '18%' }}
              />
              <Column
                key="name"
                field="name"
                header={intl.formatMessage({ id: 'chemicals.product' })}
                sortable={true}
                style={{ width: '32%' }}
              />
              <Column
                key="supplier"
                field="supplier"
                header={intl.formatMessage({ id: 'common.supplier' })}
                sortable={true}
                style={{ width: '25%' }}
              />
              <Column
                style={{ width: '15%' }}
                body={TableMarkings}
                header={intl.formatMessage({ id: 'common.marking' })}
                sortable={true}
              />
              <Column
                header={intl.formatMessage({ id: 'common.add' })}
                sortable={false}
                body={addBody}
                style={{ width: '10%' }}
              />
            </ResultsTable>
          </TableFrame>
        </Panel>
      </Section>

      <Section>
        <Panel>
          <h3>
            <FormattedMessage id="inventory.register-new-product-title"/>
          </h3>
          <p>
            <FormattedMessage id="inventory.register-new-product-text"/>
          </p>
          <Button
            primary
            label={intl.formatMessage({ id: 'inventory.register-new-product-button' })}
            onClick={handleRegisterNewProduct}
          />
        </Panel>
      </Section>

      <ModalActionsFooter>
        <Spacer/>
        <Button
          label={intl.formatMessage({ id: 'common.cancel' })}
          onClick={modalController.close}
        />
      </ModalActionsFooter>
    </div>
  )
}
